import React from 'react';
import { Droplets, Wind, Package, Activity, Recycle, Battery, Factory } from 'lucide-react';
import { mockData } from '../mock';

const iconMap = {
  Droplets, Wind, Package, Activity, Recycle, Battery, Factory
};

const FarmTechnology = () => {
  return (
    <section id="technology" className="py-20 bg-gradient-to-b from-emerald-50/30 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {mockData.farmTechnology.title}
          </h2>
          <p className="text-xl text-emerald-700 font-medium mb-4">
            {mockData.farmTechnology.subtitle}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            We leverage cutting-edge automation and monitoring systems to ensure optimal conditions for our layers, resulting in superior egg quality and farm efficiency.
          </p>
        </div>

        {/* Technology Image */}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl relative">
          <img
            src="/equipment/gartech-ultima-air-1.webp"
            alt="Gartech GE ULTIMA AIR smart layer housing with EC control"
            className="w-full h-[400px] object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
            <p className="text-white font-medium">Smart environment-controlled layer housing — Gartech GE ULTIMA AIR with EC Control</p>
          </div>
        </div>

        {/* Technology Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {mockData.farmTechnology.technologies.map((tech, index) => {
            const Icon = iconMap[tech.icon];
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-xl transition-all hover:scale-105 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform">
                  <Icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {tech.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Equipment Showcase */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Farm Partners</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div data-testid="equipment-gartech-card" className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <img
                src="/equipment/gartech-layer1.webp"
                alt="Gartech GE ULTIMA AIR layer farm equipment"
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Gartech GE ULTIMA AIR</h4>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Our layer houses run on advanced Gartech GE ULTIMA AIR equipment for superior ventilation, bird comfort, and consistent egg production.
                </p>
                <a
                  href="https://www.gartech.co.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="equipment-gartech-link"
                  className="text-sm text-emerald-700 font-medium hover:underline"
                >
                  Equipment by Gartech — gartech.co.in
                </a>
              </div>
            </div>

            <div data-testid="equipment-lark-card" className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <img
                src="/equipment/lark-pellet-mill.jpg"
                alt="Lark Engineering feed mill equipment"
                className="w-full h-64 object-contain bg-white p-4"
              />
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Lark Engineering Feed Mill (6-10 TPH)</h4>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Fresh, precisely formulated feed is produced on-site in our Lark Engineering in-house feed mill with 6-10 TPH capacity.
                </p>
                <a
                  href="https://www.larkenggco.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="equipment-lark-link"
                  className="text-sm text-emerald-700 font-medium hover:underline"
                >
                  Equipment by Lark Engineering — larkenggco.com
                </a>
              </div>
            </div>

            <div data-testid="partner-skylark-card" className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl transition-shadow">
              <img
                src="/equipment/skylark-hatchery.jpeg"
                alt="Skylark Group layer chicks"
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Skylark Group Layer Birds</h4>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Our flock is sourced from Skylark Group, India's leading hatchery — healthy, high-quality layer birds bred for consistent, long production cycles.
                </p>
                <a
                  href="https://www.skylarkgroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-skylark-link"
                  className="text-sm text-emerald-700 font-medium hover:underline"
                >
                  Birds by Skylark Group — skylarkgroup.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Technology Highlights */}
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-2xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">100% Automated Operations</h3>
            <p className="text-emerald-100 leading-relaxed mb-4">
              From feeding to egg collection, every process is computer-controlled for consistency and efficiency. Our automated systems operate 24/7, ensuring optimal conditions at all times.
            </p>
            <div className="flex items-center gap-2 text-emerald-200">
              <Activity size={20} />
              <span className="font-medium">Real-time monitoring & alerts</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-emerald-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Sustainability Focus</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Environmental responsibility is at our core. We utilize solar power, rainwater harvesting, and advanced waste management to minimize our ecological footprint while maintaining premium quality.
            </p>
            <div className="flex items-center gap-2 text-emerald-700">
              <Recycle size={20} />
              <span className="font-medium">Zero-waste farming practices</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FarmTechnology;
